import { Injectable } from '@angular/core';
import { ConfigurationService } from '@sondermind/configuration';

import { EmptyURIValue } from '../consts/consts';
import { URIPrefix } from '../enums/url-prefix.enum';
import { encodeSpecialCharacters } from '../functions/fixed-encode-uri-component';

@Injectable({
  providedIn: 'root'
})
export class DirectoryLinkService {
  constructor(
    private configurationService: ConfigurationService,
  ){}

  /**
   * Creates links to the directory service, which take in route parameters for every input.
   * Accepts optional arguments for each input.
   */
  buildProviderDirectoryLink(
    stateAbbreviation?: string,
    city?: string,
    insuranceTitle?: string,
    genderStrings?: string[],
    modalityStrings?: string[],
    specialtyStrings?: string[],
    ageStrings?: string[],
  ): string {
    // use the directory base from the config service, but default to prod if there's trouble
    const prodBaseUrl = 'https://www.sondermind.com/provider-directory';
    const baseUrl = this.configurationService.env.providerDirectoryBase ?? prodBaseUrl;

    let result = baseUrl;
    result = result.concat(`${this.createStateParam(stateAbbreviation)}`);
    result = result.concat(`${this.createCityParam(city)}/`);

    if (insuranceTitle) {
      result = result.concat(`${this.createInsuranceParam(insuranceTitle)}`);
    }

    // if there are modalities, genders, or specialties, those are separated with ONE slash.
    // after that, each TYPE is separated by a period.
    // and each PARAM within a TYPE is separated by a comma.

    // ex. ins-aetna.mod-gestalt,freudian.spc-trauma,personality

    const modalityParams = modalityStrings && modalityStrings.length > 0
      ? this.createModalitiesParam(modalityStrings)
      : '';

    const specialtyParams = specialtyStrings && specialtyStrings.length > 0
      ? this.createSpecialtiesParam(specialtyStrings)
      : '';

    const genderParams = genderStrings && genderStrings.length > 0
      ? this.createGendersParam(genderStrings)
      : '';

    const ageParams = ageStrings && ageStrings.length > 0
      ? this.createAgesParam(ageStrings)
      : '';

    // if there are modalities, specialties, or genders to include, pass that off to be handled
    // separately
    if (modalityParams || specialtyParams || genderParams || ageParams) {
      if (insuranceTitle) {
        result = result.concat('.');
      }
      result = result.concat(this.concatenateBonusParams([modalityParams, specialtyParams, genderParams, ageParams]));
    }
    return result;
  }

  // modality, specialty, and gender params are concatenated a bit differently.
  // where state, city, and insurance are all separated by slashes,
  // these param sets are comma-separated lists which are separated by periods
  // here, bonus param strings are "strings of extra parameters, themselves separated by commas
  concatenateBonusParams(bonusParams: string[]): string {
    // filter out everything empty and separate the results with periods
    const result = bonusParams
      .filter((param: string) => param?.trim())
      .join('.');

    // return with a slash before it to separate from earlier params
    return `${result}`;
  }

  private createCityParam(city?: string): string {
    const cityValue = city
      ? this.encodeString(city)
      : EmptyURIValue;

    // add a slash before the city param, which always has a value (even if it is the emptyUriValue)
    return `/${URIPrefix.CITY}-${cityValue}`;
  }

  private createGendersParam(genderStrings: string[]): string {
    const commaSeparatedGenders = genderStrings
      .filter((gender) => !!gender)
      .map(
        (gender) => `${URIPrefix.GENDER}-${this.encodeString(gender)}`
      ).join(',');

    // add a period before the genders params
    return commaSeparatedGenders;
  }

  private createInsuranceParam(insuranceTitle: string): string {
    if (insuranceTitle) {
      const encodedInsurance = this.encodeString(insuranceTitle);

      // add a slash before the insurance param
      return `${URIPrefix.INSURANCE}-${encodedInsurance}`;
    }

    // return nothing if there's no title
    return '';
  }

  private createModalitiesParam(modalities: string[]): string {
    const commaSeparatedModalities = modalities
      .filter((modality) => !!modality)
      .map(
        (modality) => `${URIPrefix.MODALITY}-${this.encodeString(modality)}`
      ).join(',');

    // add a period before the modalities params
    return commaSeparatedModalities;
  }

  private createAgesParam(ages: string[]): string {
    const commaSeparatedAges = ages
      .filter((age) => !!age)
      .map(
        (age) => `${URIPrefix.AGE}-${this.encodeString(age)}`
      ).join(',');

    return commaSeparatedAges;
  }

  private createSpecialtiesParam(specialties: string[]): string {
    // separate all of the specialties with commas
    const commaSeparatedSpecialties = specialties
      .filter((specialty) => !!specialty)
      .map(
        (specialty) => `${URIPrefix.SPECIALTY}-${this.encodeString(specialty)}`
      ).join(',');

    return `${commaSeparatedSpecialties}`;
  }

  private createStateParam(stateAbbreviation?: string): string {
    const stateValue = stateAbbreviation
      ? stateAbbreviation.toLocaleLowerCase()
      : EmptyURIValue;

    // add a slash before the state (state is always included, even as an emptyUriValue)
    return `/${URIPrefix.STATE}-${stateValue}`;
  }

  // DRIFT WARNING: this two-pass slug encoding — toLocaleLowerCase() then
  // encodeURIComponent(encodeSpecialCharacters(...)) — is mirrored in sitemap-service
  // (src/sources/provider-directory/url-builder.ts), guarded by its slug-contract spec.
  // NOTE: the sitemap mirror lowercases with toLowerCase() (locale-independent), not
  // toLocaleLowerCase(); the two agree on ASCII input but can diverge on locale-sensitive
  // characters. Keep the special-char + URI passes identical or the sitemap's canonical
  // URLs stop matching the ones this service builds.
  private encodeString(input: string): string {
    return encodeURIComponent(encodeSpecialCharacters(input.toLocaleLowerCase()));
  }
}
